import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Stack, 
  Typography 
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducers';

import SearchBar from '../../../Components/SearchBar';
import TripCategoryFilter from '../../../Components/TripCategoryFilter';
import TripCard from '../TripCard';
import BottomModal from '../../../Components/BottomModel';
import { fetchCategories } from '../../../services/CommonApiCall';
import { fetchExploreTrips } from '../../../services/CommonFunctions';
import { ITrip } from '../Trips';
import { useDebounce } from '../../../Utils/debounce';

type FilterType = 'all' | 'trip' | 'itinerary';

interface ExploreTabProps {
  onTripClick: (trip: any) => void;
}

const ExploreTab: React.FC<ExploreTabProps> = ({ onTripClick }) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000); // 300ms debounce delay
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [typeFilter, setTypeFilter] = useState<FilterType>('all');
  const [pendingTypeFilter, setPendingTypeFilter] = useState<FilterType>('all');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filteredTrips, setFilteredTrips] = useState<ITrip[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const categories = useSelector((state: RootState) => state.category?.categories || []);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchCategories(dispatch, 'trip-itinerary');
  }, [dispatch]);

  // Fetch trips with pagination
  const fetchTrips = useCallback(async (page: number = 1, reset: boolean = false, typeOverride?: FilterType) => {
    setLoading(true);
    if (reset) {
      setInitialLoading(true);
    }

    try {
      const activeType = typeOverride ?? typeFilter;
      const result = await fetchExploreTrips(
        page, 
        15, // limit
        debouncedSearchTerm || undefined, 
        selectedCategory !== 'All' ? selectedCategory : undefined,
        activeType !== 'all' ? activeType : undefined
      );

      if (result.data) {
        if (reset) {
          setFilteredTrips(result.data);
        } else {
          setFilteredTrips(prev => [...prev, ...result.data]);
        }
        
        setHasMore(result.hasMore);
      }
    } catch (err) {
      console.log('error', err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [debouncedSearchTerm, selectedCategory, typeFilter]);

  // Initial load and reset when debounced search/category changes
  useEffect(() => {
    setCurrentPage(1);
    setFilteredTrips([]);
    fetchTrips(1, true);
  }, [debouncedSearchTerm, selectedCategory, typeFilter, fetchTrips]);

  // Load more function
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchTrips(nextPage, false);
    }
  }, [loading, hasMore, currentPage, fetchTrips]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadMore]);

  const handleOpenFilterModal = () => {
    setPendingTypeFilter(typeFilter);
    setFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
  };

  const handleFilterSelect = (type: FilterType) => {
    setPendingTypeFilter(type);
    setTypeFilter(type);
    setFilterModalOpen(false);
  };

  return (
    <>
      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        onSearchClick={() => fetchTrips(1, true)}
        onFilterClick={handleOpenFilterModal}
        placeholder="Search all trips and itineraries"
      />

      <BottomModal
        open={filterModalOpen}
        onClose={handleCloseFilterModal}
        title="Filter by Type"
        maxHeight="auto"
        minHeight="auto"
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {(['all', 'trip', 'itinerary'] as FilterType[]).map((type) => (
              <Box
                key={type}
                onClick={() => handleFilterSelect(type)}
                sx={{
                  p: '2px',
                  color: typeFilter === type ? '#FEB380' : 'black',
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#FEB380',
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: typeFilter === type ? 600 : 400,
                    color: typeFilter === type ? '#FEB380' : 'text.secondary',
                  }}
                >
                  {type === 'all' ? 'All' : type === 'trip' ? 'Trip' : 'Itinerary'}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </BottomModal>

      <TripCategoryFilter
        categories={categories}
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
      />

      {initialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
            }}
          >
            {filteredTrips.map((trip) => (
              <TripCard
                key={trip._id}
                trip={trip}
                variant="explore"
                onClick={onTripClick}
              />
            ))}
          </Box>

          {/* Infinite Scroll Trigger */}
          {hasMore && (
            <Box 
              ref={loadMoreRef}
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                py: 3,
                minHeight: '60px'
              }}
            >
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading more trips...
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* No more data message */}
          {!hasMore && filteredTrips.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No more trips to load
              </Typography>
            </Box>
          )}

          {/* No data message */}
          {!initialLoading && filteredTrips.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No trips found
              </Typography>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default ExploreTab;
